import type {
  ConsoleSubscription,
  ListProSubscriptionsResponse,
  SubscriptionPayment,
  SubscriptionProviderEvent,
} from "@wallyo/contracts/console/billing";
import { t } from "../../i18n";
import { formatCountry, formatDateTime, formatMoney } from "../../i18n/format";
import {
  canEndNow,
  canExtend,
  endReasonName,
  priceNowLabel,
  reductionLabel,
  subscriptionStatusName,
  PROVIDER_EVENT_TONE,
  SUBSCRIPTION_STATUS_TONE,
  type BillingListState,
} from "../billing/billing-model";
import { discountForProHref } from "../billing/billing-route";
import { FailedCard, LoadingCard, RefreshBanner } from "../billing/BillingNotices";
import { PAYMENT_STATUS_TONE, paymentStatusName } from "../payments/payment-format";
import { ALL_PAYMENTS, paymentsHref } from "../payments/payment-route";

export type SubscriptionViewProps = {
  state: BillingListState<ListProSubscriptionsResponse>;
  /** The pro profile the subscriptions belong to, for the links out. */
  workerId: string;
  canManage: boolean;
  canOfferDiscounts: boolean;
  onExtend: (subscription: ConsoleSubscription) => void;
  onEndNow: (subscription: ConsoleSubscription) => void;
  onRetry: () => void;
};

/**
 * Abonnement, on a pro's page: their credit, then each subscription newest
 * first with every payment and provider event behind it (ADR-0018 §10).
 * There are no refunds in the subscription model: a goodwill gesture is a
 * credit, issued from Paiements.
 */
export function SubscriptionView(props: SubscriptionViewProps) {
  const { state } = props;
  if (!state.data && state.status === "failed") {
    return <FailedCard text={t("billing.sub.loadFailed")} error={state.error} onRetry={props.onRetry} />;
  }
  if (!state.data) return <LoadingCard text={t("billing.sub.loading")} />;

  const { creditBalances, subscriptions } = state.data;
  return (
    <div className="card" data-subscriptions="">
      <RefreshBanner show={state.status === "failed"} onRetry={props.onRetry} />
      <ul className="kv">
        <li>
          <span>{t("billing.sub.credit")}</span>
          <span data-credit="">
            <Credit balances={creditBalances} />
          </span>
        </li>
      </ul>
      <p className="foot">{t("billing.sub.noRefunds")}</p>
      <div className="btns">
        <a className="go-link" href={paymentsHref({ ...ALL_PAYMENTS, workerId: props.workerId })}>
          {t("billing.sub.creditLink")}
        </a>
        <DiscountLink show={props.canOfferDiscounts} workerId={props.workerId} />
      </div>
      <NoSubscriptions show={subscriptions.length === 0} />
      {subscriptions.map((subscription) => (
        <SubscriptionCard key={subscription.subscriptionId} subscription={subscription} {...props} />
      ))}
    </div>
  );
}

function Credit({ balances }: { balances: readonly { currency: string; balance: number }[] }) {
  if (balances.length === 0) return <>{t("billing.sub.noCredit")}</>;
  return <>{balances.map((each) => formatMoney(each.balance, each.currency)).join(" · ")}</>;
}

function DiscountLink({ show, workerId }: { show: boolean; workerId: string }) {
  if (!show) return null;
  return (
    <a className="go-link" href={discountForProHref(workerId)}>
      {t("billing.sub.discountLink")}
    </a>
  );
}

function NoSubscriptions({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <p className="foot" role="status" data-empty="subscriptions">
      {t("billing.sub.none")}
    </p>
  );
}

function SubscriptionCard({
  subscription,
  canManage,
  onExtend,
  onEndNow,
}: SubscriptionViewProps & { subscription: ConsoleSubscription }) {
  return (
    <div className="verif-detail" data-subscription={subscription.subscriptionId}>
      <div className="verif-head">
        <div>
          <h3>{subscription.planName}</h3>
          <p className="meta">{t("billing.sub.since", { date: formatDateTime(subscription.createdAt) })}</p>
        </div>
        <span className="verif-pills">
          <span className={`pill ${SUBSCRIPTION_STATUS_TONE[subscription.status]}`} data-status={subscription.status}>
            {subscriptionStatusName(subscription.status)}
          </span>
          <Entitled entitled={subscription.entitled} />
          <span className="meta mono" title={t("billing.sub.versionTitle")}>
            v{subscription.version}
          </span>
        </span>
      </div>
      <Facts subscription={subscription} />
      <h2 className="verif-h">{t("billing.sub.payments")}</h2>
      <Payments payments={subscription.payments} />
      <h2 className="verif-h">{t("billing.sub.events")}</h2>
      <Events events={subscription.providerEvents} />
      <Actions subscription={subscription} canManage={canManage} onExtend={onExtend} onEndNow={onEndNow} />
    </div>
  );
}

function Entitled({ entitled }: { entitled: boolean }) {
  if (entitled) {
    return (
      <span className="pill ok" data-entitled="true">
        {t("billing.sub.entitled")}
      </span>
    );
  }
  return (
    <span className="pill bad" data-entitled="false">
      {t("billing.sub.notEntitled")}
    </span>
  );
}

function Facts({ subscription }: { subscription: ConsoleSubscription }) {
  return (
    <ul className="kv">
      <li>
        <span>{t("billing.sub.provider")}</span>
        <span>
          {subscription.provider} · <Renewal auto={subscription.renewsAutomatically} />
        </span>
      </li>
      <li>
        <span>{t("billing.sub.country")}</span>
        <span>{formatCountry(subscription.country)}</span>
      </li>
      <li>
        <span>{t("billing.sub.price")}</span>
        <span data-price="">{priceNowLabel(subscription)}</span>
      </li>
      <Reduction subscription={subscription} />
      <NextPlan name={subscription.nextPlanName} />
      <When label={t("billing.sub.paidThrough")} when={subscription.paidThrough} mark="paid-through" />
      <When label={t("billing.sub.renewalCancelled")} when={subscription.renewalCancelledAt} mark="cancelled" />
      <When label={t("billing.sub.graceEnds")} when={subscription.graceEndsAt} mark="grace" />
      <When label={t("billing.sub.ended")} when={subscription.endedAt} mark="ended" />
      <EndReason subscription={subscription} />
    </ul>
  );
}

function Renewal({ auto }: { auto: boolean }) {
  if (auto) return <>{t("billing.sub.renewsAutomatically")}</>;
  return <>{t("billing.sub.paidByHand")}</>;
}

function When({ label, when, mark }: { label: string; when: string | null; mark: string }) {
  if (!when) return null;
  return (
    <li>
      <span>{label}</span>
      <span data-when={mark}>{formatDateTime(when)}</span>
    </li>
  );
}

function Reduction({ subscription }: { subscription: ConsoleSubscription }) {
  const label = reductionLabel(subscription);
  if (!label) return null;
  return (
    <li>
      <span>{t("billing.sub.reduction")}</span>
      <span data-reduction="">
        <bdi>{label}</bdi>
      </span>
    </li>
  );
}

function NextPlan({ name }: { name: string | null }) {
  if (!name) return null;
  return (
    <li>
      <span>{t("billing.sub.nextPlan")}</span>
      <span data-next-plan="">{t("billing.sub.nextPlanValue", { plan: name })}</span>
    </li>
  );
}

function EndReason({ subscription }: { subscription: ConsoleSubscription }) {
  if (!subscription.endReason) return null;
  return (
    <li>
      <span>{t("billing.sub.endReason")}</span>
      <span data-end-reason={subscription.endReason}>{endReasonName(subscription.endReason)}</span>
    </li>
  );
}

function Payments({ payments }: { payments: readonly SubscriptionPayment[] }) {
  if (payments.length === 0) return <p className="foot">{t("billing.sub.noPayments")}</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th className="num">{t("billing.col.amount")}</th>
            <th className="num">{t("billing.col.amountDue")}</th>
            <th>{t("billing.col.status")}</th>
            <th>{t("billing.col.period")}</th>
            <th>{t("billing.col.date")}</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={payment.platformFeePaymentId} data-payment={payment.platformFeePaymentId}>
              <td className="num">{formatMoney(payment.amount, payment.currency)}</td>
              <td className="num">
                <AmountDue payment={payment} />
              </td>
              <td>
                <span className={`pill ${PAYMENT_STATUS_TONE[payment.status]}`}>
                  {paymentStatusName(payment.status)}
                </span>
              </td>
              <td className="nowrap">
                <Period payment={payment} />
              </td>
              <td className="nowrap">{formatDateTime(payment.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AmountDue({ payment }: { payment: SubscriptionPayment }) {
  if (payment.amountDue === null) return <span className="meta">—</span>;
  return <>{formatMoney(payment.amountDue, payment.currency)}</>;
}

function Period({ payment }: { payment: SubscriptionPayment }) {
  if (!payment.periodStart || !payment.periodEnd) return <span className="meta">{t("billing.sub.noPeriod")}</span>;
  return (
    <>{t("billing.window", { from: formatDateTime(payment.periodStart), to: formatDateTime(payment.periodEnd) })}</>
  );
}

function Events({ events }: { events: readonly SubscriptionProviderEvent[] }) {
  if (events.length === 0) return <p className="foot">{t("billing.sub.noEvents")}</p>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t("billing.col.date")}</th>
            <th>{t("billing.col.provider")}</th>
            <th>{t("billing.col.eventKind")}</th>
            <th>{t("billing.col.via")}</th>
            <th>{t("billing.col.result")}</th>
            <th>{t("billing.col.detail")}</th>
            <th>{t("billing.col.eventRef")}</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.providerEventId} data-event={event.providerEventId}>
              <td className="nowrap">{formatDateTime(event.receivedAt)}</td>
              <td>{event.provider}</td>
              <td>{t(`billing.event.${event.kind}` as const)}</td>
              <td>{t(`billing.via.${event.via}` as const)}</td>
              <td>
                <span className={`pill ${PROVIDER_EVENT_TONE[event.result]}`} data-result={event.result}>
                  {t(`billing.result.${event.result}` as const)}
                </span>
              </td>
              <td>
                <Detail detail={event.detail} />
              </td>
              <td className="mono">{event.eventRef}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Detail({ detail }: { detail: string | null }) {
  if (!detail) return <span className="meta">—</span>;
  return <bdi>{detail}</bdi>;
}

function Actions({
  subscription,
  canManage,
  onExtend,
  onEndNow,
}: {
  subscription: ConsoleSubscription;
  canManage: boolean;
  onExtend: (subscription: ConsoleSubscription) => void;
  onEndNow: (subscription: ConsoleSubscription) => void;
}) {
  if (!canManage) return null;
  return (
    <div className="case-actions">
      <h2 className="verif-h">{t("billing.sub.actions")}</h2>
      <div className="btns">
        <button
          className="btn"
          type="button"
          disabled={!canExtend(subscription)}
          data-action="extend"
          onClick={() => onExtend(subscription)}
        >
          {t("billing.action.extendSubscription")}
        </button>
        <button
          className="btn danger"
          type="button"
          disabled={!canEndNow(subscription)}
          data-action="endSubscription"
          onClick={() => onEndNow(subscription)}
        >
          {t("billing.action.endSubscription")}
        </button>
      </div>
      <p className="foot">{t("billing.sub.actionsFoot")}</p>
    </div>
  );
}
